import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Vehicles from "@/pages/Vehicles";
import Drivers from "@/pages/Drivers";
import Vendors from "@/pages/Vendors";
import Clients from "@/pages/Clients";
import Trips from "@/pages/Trips";
import VendorAccounting from "@/pages/VendorAccounting";
import CalendarPage from "@/pages/CalendarPage";
import Leads from "@/pages/Leads";
import Reviews from "@/pages/Reviews";
import Invoices from "@/pages/Invoices";
import Lottery from "@/pages/Lottery";
import DriverPortal from "@/pages/DriverPortal";
import DriverAdmin from "@/pages/DriverAdmin";
import ClientPortal from "@/pages/ClientPortal";
import ClientAssignments from "@/pages/ClientAssignments";
import Users from "@/pages/Users";
import SalesAgents from "@/pages/SalesAgents";
import SalesPortal from "@/pages/SalesPortal";
import DriverSubmissions from "@/pages/DriverSubmissions";
import Settings from "@/pages/Settings";

function RoleGate({ children, block = [], to = "/" }) {
  const { user } = useAuth();
  if (user && block.includes(user.role)) return <Navigate to={to} replace />;
  return children;
}

function Private({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "driver") return <Navigate to="/driver-portal" replace />;
  if (user.role === "client") return <Navigate to="/client-portal" replace />;
  if (user.role === "sales") return <Navigate to="/sales-portal" replace />;
  return <Navigate to="/dashboard" replace />;
}

function Root() {
  const loc = useLocation();
  // Handle Emergent Google Auth callback with #session_id in the URL fragment
  if (loc.hash && loc.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<Private><Layout /></Private>}>
        <Route path="/dashboard" element={<RoleGate block={["sales","driver","client","vendor"]}><Dashboard /></RoleGate>} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/drivers" element={<Drivers />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/trips" element={<Trips />} />
        <Route path="/vendor-accounting" element={<VendorAccounting />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/lottery" element={<Lottery />} />
        <Route path="/users" element={<Users />} />
        <Route path="/sales-agents" element={<SalesAgents />} />
        <Route path="/sales-portal" element={<SalesPortal />} />
        <Route path="/driver-submissions" element={<RoleGate block={["driver","client","sales","vendor"]}><DriverSubmissions /></RoleGate>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/driver-portal" element={<DriverPortal />} />
        <Route path="/driver-admin" element={<DriverAdmin />} />
        <Route path="/client-portal" element={<ClientPortal />} />
        <Route path="/client-assignments" element={<ClientAssignments />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <ConfirmProvider>
          <Toaster position="top-right" theme="dark" />
          <Root />
        </ConfirmProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
